/*
 * Pincode delivery estimate.
 *
 * Looks up one pincode at a time in the "Pincodes" tab of the delivery Google Sheet (zone, city
 * and active flag) instead of downloading the whole sheet, then works out dispatch and delivery dates from the theme settings: cutoff
 * times, delivery time for Hyderabad, transit days for the rest of India, weekly off days and
 * holiday dates. The sheet's Avg. Delivery Time column is not used.
 * All calendar maths is done in IST (Asia/Kolkata), whatever the visitor's device timezone.
 *
 * Pincodes tab columns: A Pincode | B City | C State | D Zone | E Delivery Type
 *                       | F Avg. Delivery Time (unused) | G Active (optional, "N" disables)
 *
 * Usage: PincodeDelivery.init(config); PincodeDelivery.load('500034').then(() => PincodeDelivery.lookup('500034'))
 */
(function () {
  var SHEET_ID = '1rsK8SfVDNqa7HQqJ67Tj9klEXc460yuFcZUAZKpINEA';
  // JSON {pin, zone, city, t}; theme.js (StorePickup) reads it too. v2 so the old live 'pincode' key (a bare
  // string + 'pincodeType') is never read or changed by this code.
  var SAVED_KEY = 'pincode:v2';
  var CACHE_TTL = 6 * 60 * 60 * 1000;
  var DAY_MS = 86400000;

  var config = {};
  var pins = {}; // pincode -> [zone, hours, active, city], or null when unknown. This page view only.
  var inflight = {}; // pincode -> pending fetch
  var holidays = []; // day numbers, parsed from the theme setting
  var scheduleBlocked = []; // day numbers the cart date picker can't offer

  /* ---------- date helpers (a "day" is an integer count of days since 1970-01-01) ---------- */

  function toDay(y, m, d) {
    return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);
  }

  function weekday(day) {
    return (day + 4) % 7; // 1970-01-01 was a Thursday; 0 = Sunday
  }

  function parseTime(str, fallbackMinutes) {
    var m = /^\s*(\d{1,2})[:.](\d{2})\s*$/.exec(str || '');
    if (!m) return fallbackMinutes;
    return Math.min(23, +m[1]) * 60 + Math.min(59, +m[2]);
  }

  // Accepts YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, or anything Date.parse understands.
  function parseDay(str) {
    str = String(str || '').trim();
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(str);
    if (m) return toDay(+m[1], +m[2], +m[3]);
    m = /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/.exec(str);
    if (m) return toDay(+m[3], +m[2], +m[1]);
    var t = Date.parse(str + ' UTC');
    return isNaN(t) ? null : Math.floor(t / DAY_MS);
  }

  // Current wall-clock time in IST. `?eta_now=2026-10-03T12:50` overrides it for testing.
  function nowIST() {
    var override = /[?&]eta_now=(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(window.location.search);
    if (override) {
      return { day: toDay(+override[1], +override[2], +override[3]), minutes: +override[4] * 60 + +override[5] };
    }
    var parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata', hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
    }).formatToParts(new Date()).forEach(function (p) { parts[p.type] = +p.value; });
    return {
      day: toDay(parts.year, parts.month, parts.day),
      minutes: (parts.hour % 24) * 60 + parts.minute,
      seconds: parts.second
    };
  }

  /* ---------- working-day rules ---------- */

  // sunOpen: the "Sunday works for Hyderabad-only products" setting applies, so Sunday isn't an off day.
  function isWorkingDay(day, sunOpen) {
    var wd = weekday(day);
    if (!(sunOpen && wd === 0) && (config.offDays || []).indexOf(wd) !== -1) return false;
    return holidays.indexOf(day) === -1;
  }

  // Theme setting: one date per line (YYYY-MM-DD), anything after the date is a free-text note.
  function parseHolidays(text) {
    var days = [];
    String(text || '').split(/\r?\n/).forEach(function (line) {
      line = line.trim();
      if (!line || line.charAt(0) === '#') return;
      var day = parseDay(line.split(/\s+/)[0]);
      if (day !== null) days.push(day);
    });
    return days;
  }

  // First working day on or after `day`. Gives up after 60 days so a bad config can't hang the page.
  function firstWorkingOnOrAfter(day, sunOpen) {
    for (var i = 0; i < 60; i++) {
      if (isWorkingDay(day + i, sunOpen)) return day + i;
    }
    return day;
  }

  function addWorkingDays(day, n, sunOpen) {
    while (n > 0) {
      day = firstWorkingOnOrAfter(day + 1, sunOpen);
      n--;
    }
    return day;
  }

  /* ---------- estimate ---------- */

  function intSetting(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) || n < 0 ? fallback : n;
  }

  // DIWALI-2026-SCHEDULE: temporary fixed delivery dates by order date, per zone. Enabled by the
  // "Diwali delivery schedule" theme setting. Remove after 8 Nov 2026.
  var DIWALI_SCHEDULE = [
    { order: toDay(2026, 11, 3), delivery: toDay(2026, 11, 5), zone: 'PANINDIA' },
    { order: toDay(2026, 11, 4), delivery: toDay(2026, 11, 6), zone: 'PANINDIA' },
    { order: toDay(2026, 11, 5), delivery: toDay(2026, 11, 6), zone: 'HYDERABAD' },
    { order: toDay(2026, 11, 6), delivery: toDay(2026, 11, 8), zone: 'HYDERABAD' }
  ];

  // DIWALI-2026-SCHEDULE: the fixed delivery day for this zone, or null. An order placed at or after
  // the zone's cutoff counts as the next day's order. Ignores off days and holidays on purpose.
  function fixedDeliveryDay(zone, now, cutoff) {
    if (!config.diwaliSchedule) return null;
    var orderDay = now.day + (now.minutes >= cutoff ? 1 : 0);
    for (var i = 0; i < DIWALI_SCHEDULE.length; i++) {
      var rule = DIWALI_SCHEDULE[i];
      if (rule.order === orderDay && rule.zone === zone) return rule.delivery;
    }
    return null;
  }

  /**
   * The date an order arrives. It is dispatched today when today is a working day and the cutoff
   * hasn't passed, otherwise on the next working day. Hyderabad orders arrive after the configured
   * working days for before-cutoff (0 = same day) or after-cutoff orders (extra buffer days, not
   * applied to Long Lasting Sweets); the rest of India arrives after the configured number of
   * transit working days. Off days and holidays are skipped throughout.
   *
   * Long Lasting Sweets (product titles containing MAP) differ:
   *   Hyderabad ......... their own cutoff
   *   Hyderabad / Pan India  separate numbers of extra working days added to the arrival date
   *                     (0 = same as other products)
   * @param {{zone: 'HYDERABAD'|'PANINDIA', isLLS: boolean, sunOk: boolean}} opts
   *   sunOk (a Pan-India product, i.e. not Hyderabad-only) plus the Sunday theme setting makes Sunday a working day for Hyderabad pincodes.
   * @returns {{deliveryDay:number, today:number, msLeft:(number|null)}}
   *   msLeft is the time left before today's cutoff, or null when dispatch isn't today.
   */
  function computeEta(opts) {
    var now = nowIST();
    var local = opts.zone === 'HYDERABAD';
    var intl = opts.zone === 'INTL';
    var cutoff = local && opts.isLLS
      ? parseTime(config.llsCutoff, 14 * 60 + 30)
      : parseTime(local ? config.cutoffLocal : config.cutoffPan, 13 * 60);

    var sunOpen = !!config.sunWorkingHydOnly && local && !!opts.sunOk;
    var dispatchToday = isWorkingDay(now.day, sunOpen) && now.minutes < cutoff;
    var dispatchDay = dispatchToday ? now.day : firstWorkingOnOrAfter(now.day + 1, sunOpen);

    var hydDays = dispatchToday ? config.hydDaysBeforeCutoff : config.hydDaysAfterCutoff;
    var transit = local ? (opts.isLLS ? 0 : intSetting(hydDays, 0)) : intl ? intSetting(config.intlTransitDays, 10) : intSetting(config.panTransitDays, 4);
    if (opts.isLLS && !intl) transit += intSetting(local ? config.llsExtraDaysLocal : config.llsExtraDays, local ? 0 : 1);

    var fixedDay = fixedDeliveryDay(opts.zone, now, cutoff); // DIWALI-2026-SCHEDULE

    return {
      zone: opts.zone,
      deliveryDay: fixedDay !== null ? fixedDay : addWorkingDays(dispatchDay, transit, sunOpen),
      sunOpen: sunOpen,
      today: now.day,
      msLeft: dispatchToday ? (cutoff - now.minutes) * 60000 - (now.seconds || 0) * 1000 : null
    };
  }

  function formatDay(day, today) {
    if (day === today) return 'Today';
    if (day === today + 1) return 'Tomorrow';
    return new Date(day * DAY_MS).toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
    });
  }

  function formatCountdown(ms) {
    var totalMin = Math.max(1, Math.ceil(ms / 60000));
    var h = Math.floor(totalMin / 60);
    var m = totalMin % 60;
    return (h ? h + 'h ' : '') + m + 'm';
  }

  /* ---------- saved pincode (the only thing kept in localStorage) ---------- */

  // Returns {pin, zone, city, t} or null.
  function readSaved() {
    try {
      var raw = localStorage.getItem(SAVED_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      return saved && /^\d{6}$/.test(saved.pin) ? saved : null;
    } catch (e) {
      return null;
    }
  }

  // Drops the old whole-sheet cache.
  function cleanStorage() {
    try {
      var stale = [];
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i);
        if (key && key.indexOf('pincodeSheet:') === 0) stale.push(key);
      }
      stale.forEach(function (key) { localStorage.removeItem(key); });
    } catch (e) { /* storage blocked */ }
  }

  /* ---------- sheet lookup ---------- */

  function titleCase(str) {
    return String(str || '').trim().toLowerCase().replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  // gviz answers `/*O_o*/ google.visualization.Query.setResponse({...});`
  function parseGviz(text) {
    var start = text.indexOf('(');
    var end = text.lastIndexOf(')');
    if (start === -1 || end <= start) throw new Error('Unexpected sheet response');
    return JSON.parse(text.slice(start + 1, end));
  }

  function cell(cells, i) {
    var c = cells[i];
    return c && c.v !== null && c.v !== undefined ? c.v : '';
  }

  // One row of the sheet, as [zone, hours, active, city], or null when the pincode isn't listed.
  function fetchPin(pin) {
    var url = 'https://docs.google.com/spreadsheets/d/' + SHEET_ID + '/gviz/tq?tqx=out:json&sheet=Pincodes&tq=' +
      encodeURIComponent('select * where A=' + pin);
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('Sheet request failed: ' + res.status);
      return res.text();
    }).then(function (text) {
      var data = parseGviz(text);
      if (data.status !== 'ok') throw new Error('Sheet query failed');
      var row = data.table.rows[0];
      if (!row) return null;
      var c = row.c;
      var zone = String(cell(c, 4)).replace(/\s/g, '').toUpperCase();
      var active = String(cell(c, 6)).trim().toUpperCase() !== 'N';
      return [zone, parseFloat(cell(c, 5)) || 0, active, titleCase(cell(c, 1))];
    });
  }

  /** Resolves once `pin` is known (looked up in the sheet unless this page already has it). */
  function load(pin) {
    if (!/^\d{6}$/.test(pin)) return Promise.resolve();
    if (pin in pins) return Promise.resolve();
    if (inflight[pin]) return inflight[pin];
    inflight[pin] = fetchPin(pin).then(function (row) {
      pins[pin] = row;
      delete inflight[pin];
    }, function (err) {
      delete inflight[pin]; // let the next check retry
      throw err;
    });
    return inflight[pin];
  }

  function has(pin) {
    return pin in pins;
  }

  function init(cfg) {
    config = cfg || {};
    // Re-read on every call so a theme-settings edit applies without waiting.
    holidays = parseHolidays(config.holidays);
    scheduleBlocked = parseHolidays(config.scheduleBlockedDates);
    return Promise.resolve();
  }

  // A pincode checked in the last 6 hours needs no network call on any page.
  cleanStorage();
  (function () {
    var saved = readSaved();
    if (saved && saved.zone && Date.now() - saved.t < CACHE_TTL) {
      pins[saved.pin] = [saved.zone, 0, true, saved.city || ''];
    }
  })();

  /** @returns {{zone:string, hours:number, city:string}|null} null when the pincode is unknown or disabled */
  function lookup(pin) {
    var row = pins[pin];
    if (!row || !row[2] || (row[0] !== 'HYDERABAD' && row[0] !== 'PANINDIA')) return null;
    return { zone: row[0], hours: row[1], city: row[3] || '' };
  }

  /**
   * Decides whether a product can be delivered to a pincode.
   * @param {string} pin
   * @param {{panIndia:boolean, notHyderabad:boolean}} flags the product's delivery tags
   * @returns {{status:'ok'|'not_deliverable'|'invalid', zone:string, info:(object|null), persist:boolean}}
   *   persist says whether the pincode should be remembered. A PANINDIA pincode on a local-only
   *   product is remembered (but not deliverable), matching what the product page has always done.
   */
  function evaluate(pin, flags) {
    if (!/^\d{6}$/.test(pin)) return { status: 'invalid', zone: '', info: null, persist: false };
    var info = lookup(pin);
    var zone = info ? info.zone : '';
    if (zone === 'HYDERABAD' && flags.panIndia && flags.notHyderabad) {
      return { status: 'not_deliverable', zone: zone, info: info, persist: false };
    }
    if (zone === 'PANINDIA' && !flags.panIndia) {
      return { status: 'not_deliverable', zone: zone, info: info, persist: true };
    }
    if (zone === 'HYDERABAD' || (zone === 'PANINDIA' && flags.panIndia)) {
      return { status: 'ok', zone: zone, info: info, persist: true };
    }
    return { status: 'not_deliverable', zone: zone, info: info, persist: false };
  }

  /**
   * Checks every item in a cart against one pincode.
   * @param {string} pin
   * @param {Array<{panIndia:boolean, notHyderabad:boolean, isLLS:boolean}>} items per-item flags
   * @returns {{info:(object|null), zone:string, deliverable:Array, blocked:Array, eta:(object|null)}}
   *   eta is the latest delivery across the deliverable items, since an order ships together.
   *   info is null when the pincode is unknown or disabled (every item is then blocked).
   */
  function evaluateCart(pin, items) {
    var info = lookup(pin);
    var result = { info: info, zone: info ? info.zone : '', deliverable: [], blocked: [], eta: null };
    if (!info) {
      result.blocked = items.slice();
      return result;
    }
    items.forEach(function (flags) {
      if (evaluate(pin, flags).status !== 'ok') {
        result.blocked.push(flags);
        return;
      }
      result.deliverable.push(flags);
      var eta = computeEta({ zone: info.zone, isLLS: flags.isLLS, sunOk: !!flags.panIndia });
      if (!result.eta) {
        result.eta = eta;
        return;
      }
      // An order ships together, so it arrives when the slowest item does. The countdown is the
      // shortest time left before any item's cutoff.
      if (eta.deliveryDay > result.eta.deliveryDay) result.eta.deliveryDay = eta.deliveryDay;
      // Sunday is only open for the cart's schedule when every item allows it.
      result.eta.sunOpen = result.eta.sunOpen && eta.sunOpen;
      if (eta.msLeft !== null && (result.eta.msLeft === null || eta.msLeft < result.eta.msLeft)) result.eta.msLeft = eta.msLeft;
    });
    return result;
  }

  /**
   * The "Order within" line from the theme setting ([time] -> bold time left, [when] -> today /
   * tomorrow / by then), or '' once today's cutoff has passed or the setting is blank.
   */
  function countdownText(eta) {
    if (eta.msLeft === null) return '';
    var setting = eta.zone === 'HYDERABAD' ? config.countdownTextLocal : config.countdownTextPan;
    var template = setting === undefined || setting === null
      ? 'Order within [time] to get it [when]'
      : String(setting).trim();
    if (!template) return '';
    var when = eta.deliveryDay === eta.today ? 'today' : eta.deliveryDay === eta.today + 1 ? 'tomorrow' : 'by then';
    return template
      .replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; })
      .replace(/\[time\]/gi, '<strong>' + formatCountdown(eta.msLeft) + '</strong>')
      .replace(/\[when\]/gi, when);
  }

  /** "Fri, 9 Oct" ("Today" / "Tomorrow" for the nearest days). */
  function arrivalText(eta) {
    return formatDay(eta.deliveryDay, eta.today);
  }

  /**
   * What the cart's date picker needs: the first day it may offer (the working day after the
   * arrival date), which days are blocked, and whether Standard delivery is switched off for the
   * zone. Days are ISO strings (YYYY-MM-DD).
   */
  function scheduleInfo(eta) {
    var minDay = firstWorkingOnOrAfter(eta.deliveryDay + 1, eta.sunOpen);
    return {
      standardOff: standardOff(eta.zone),
      arrivalISO: isoDay(eta.deliveryDay),
      minISO: isoDay(minDay),
      offDays: (config.offDays || []).filter(function (d) { return !(eta.sunOpen && d === 0); }),
      holidays: blockedISO()
    };
  }

  /** Whether the zone's "Disable Standard delivery" checkbox is ticked (takes effect immediately). */
  function standardOff(zone) {
    var key = zone === 'HYDERABAD' ? 'Hyd' : zone === 'PANINDIA' ? 'Pan' : '';
    return !!key && !!config['standardOff' + key];
  }

  /** Schedule rules with no arrival date to start from (the picker falls back to its own minimum). */
  function baseScheduleInfo() {
    return { minISO: '', offDays: (config.offDays || []).slice(), holidays: blockedISO() };
  }

  function isoDay(day) {
    return new Date(day * DAY_MS).toISOString().slice(0, 10);
  }

  // Holidays plus the dates switched off for scheduling, de-duplicated.
  function blockedISO() {
    var seen = {};
    return holidays.concat(scheduleBlocked).map(isoDay).filter(function (iso) {
      if (seen[iso]) return false;
      seen[iso] = true;
      return true;
    });
  }

  /** Arrival estimate for shoppers outside India (Pan-India cutoff, international transit days). */
  function intlEta() {
    return computeEta({ zone: 'INTL', isLLS: false });
  }

  function savedPincode() {
    var saved = readSaved();
    return saved ? saved.pin : null;
  }

  // One localStorage key, read by theme.js (StorePickup) as well.
  // `source` lets a listener ignore the change it caused itself.
  function remember(pin, zone, city, source) {
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify({ pin: pin, zone: zone, city: city || '', t: Date.now() }));
    } catch (e) { /* storage blocked */ }
    document.dispatchEvent(new CustomEvent('pincode:changed', { detail: { pin: pin, zone: zone, source: source || '' } }));
  }

  function forget() {
    try {
      localStorage.removeItem(SAVED_KEY);
    } catch (e) { /* storage blocked */ }
  }

  window.PincodeDelivery = {
    init: init,
    load: load,
    has: has,
    lookup: lookup,
    evaluate: evaluate,
    evaluateCart: evaluateCart,
    arrivalText: arrivalText,
    countdownText: countdownText,
    formatCountdown: formatCountdown,
    scheduleInfo: scheduleInfo,
    baseScheduleInfo: baseScheduleInfo,
    intlEta: intlEta,
    savedPincode: savedPincode,
    savedPincodeInfo: readSaved,
    remember: remember,
    forget: forget,
    computeEta: computeEta,
    formatDay: formatDay
  };
})();

/*
 * Pincode gate for Add to Cart buttons.
 *
 * Catches add-to-cart submits (and the USF plugin button's click) in the capture phase,
 * before the theme's product-form or USF handlers run. When there is no saved pincode that
 * works for the product, it opens #pincode-gate (snippets/pincode-gate.liquid) and replays the
 * add once the shopper enters a deliverable pincode.
 *
 * A product's delivery tags come from a hidden <input data-pincode-flags> inside each form
 * (snippets/pincode-flags.liquid, and the USF templates). Forms without it are left alone.
 */
(function () {
  var dialog = document.getElementById('pincode-gate');
  var configEl = document.getElementById('pincode-delivery-config-global');
  if (!dialog || !configEl || !window.customElements.get('modal-content') || !window.PincodeDelivery) return;

  var PD = window.PincodeDelivery;
  var cfg = JSON.parse(configEl.textContent);

  var formEl = dialog.querySelector('[data-pincode-gate-form]');
  var inputEl = dialog.querySelector('[data-pincode-gate-input]');
  var submitBtn = formEl.querySelector('button[type="submit"]');
  var errorEl = dialog.querySelector('.pincode-gate__error');
  var whereEl = dialog.querySelector('[data-pincode-gate-where]');
  var etaEl = dialog.querySelector('[data-pincode-gate-eta]');

  var ICON_TRUCK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2m0 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m6 0H9m6 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m0 0h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // An add waiting on a pincode: { form, submitter, mode: 'submit' | 'click', flags }.
  // Or, when opened from the cart's Change link: { mode: 'cart', anchor, items }.
  var pending = null;
  var proceedTimer = null;
  var opener = null;
  var pausedTraps = [];

  /* ---------- which adds does the gate apply to? ---------- */

  function isCartAddForm(form) {
    return form instanceof HTMLFormElement && (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
  }

  // Shoppers outside India don't use pincodes (custom.js hides the checker for them too).
  function isInternationalVisitor() {
    try {
      var country = localStorage.getItem('userCountry');
      return !!country && country !== 'IN';
    } catch (e) {
      return false;
    }
  }

  function readFlags(form) {
    var el = form.querySelector('[data-pincode-flags]');
    return el ? flagsFromEl(el) : null;
  }

  function flagsFromEl(el) {
    return {
      title: el.getAttribute('data-title') || '',
      panIndia: el.getAttribute('data-pan-india') === 'true',
      notHyderabad: el.getAttribute('data-not-hyderabad') === 'true',
      isLLS: el.getAttribute('data-lls') === 'true'
    };
  }

  /**
   * Returns the gate context for an add attempt, or null when the add may go straight through.
   */
  function needsGate(form) {
    if (!isCartAddForm(form)) return null;
    if (form.hasAttribute('data-pincode-bypass')) return null;
    if (isInternationalVisitor()) return null;
    var flags = readFlags(form);
    if (!flags) return null;

    var saved = PD.savedPincode();
    if (saved && PD.has(saved) && PD.evaluate(saved, flags).status === 'ok') return null;
    return { flags: flags, saved: saved };
  }

  /* ---------- replaying the add ---------- */

  function themeSetting(name) {
    var settings = window.themeVariables && window.themeVariables.settings;
    return settings ? settings[name] : undefined;
  }

  function themeRoute(name, fallback) {
    var routes = window.themeVariables && window.themeVariables.routes;
    return (routes && routes[name]) || fallback;
  }

  function fire(target, name, detail, cancelable) {
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: !!cancelable, detail: detail }));
  }

  /**
   * Adds the form's product to the cart over AJAX and opens the cart drawer, mirroring what the
   * theme's ProductForm does on the product page. Used when no handler on the form claims the add,
   * so a replayed add can never fall back to a full-page form post.
   */
  function addDirect(form, submitter) {
    var buttons = submitter ? [submitter] : Array.prototype.filter.call(form.elements, function (el) {
      return el.type === 'submit';
    });
    function setBusy(busy) {
      buttons.forEach(function (button) {
        if (busy) {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        }
      });
    }
    function notify(status, error) {
      fire(form, 'cart-notification:show', { status: status, error: error || '' }, true);
    }

    var body = new FormData(form);
    body.append('sections', 'mini-cart');
    ['option1', 'option2', 'option3'].forEach(function (key) { body.delete(key); });

    setBusy(true);
    fetch(themeRoute('cartAddUrl', '/cart/add') + '.js', {
      method: 'POST',
      body: body,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      return response.json().then(function (json) { return { ok: response.ok, json: json }; });
    }).then(function (result) {
      setBusy(false);
      if (!result.ok) {
        notify('error', result.json && result.json.description);
        return;
      }
      if (themeSetting('cartType') === 'page' || themeSetting('pageType') === 'cart') {
        window.location.href = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart';
        return;
      }
      fire(form, 'variant:added', { variant: result.json.items ? result.json.items[0] : result.json });
      return fetch(themeRoute('cartUrl', '/cart') + '.js').then(function (response) {
        return response.json();
      }).then(function (cart) {
        fire(document.documentElement, 'cart:updated', { cart: cart });
        cart.sections = result.json.sections;
        fire(document.documentElement, 'cart:refresh', {
          cart: cart,
          openMiniCart: themeSetting('cartType') === 'drawer' && (!form.closest('.drawer') || !!form.closest('quick-buy-drawer'))
        });
        notify('success');
      });
    }).catch(function () {
      setBusy(false);
      notify('error', 'Something went wrong. Please try again.');
    });
  }

  /**
   * Replays an approved add. It dispatches a synthetic submit event, which runs the same handlers
   * as a real one (the theme's ProductForm, USF's Vue handler) but never navigates, so a missing
   * preventDefault() can't reload the page. If nothing claims the event, addDirect() does the add.
   */
  function proceed() {
    var p = pending;
    pending = null;
    if (!p || !p.form || !document.contains(p.form)) return;

    if (!p.form.checkValidity()) {
      p.form.reportValidity();
      return;
    }
    if (p.mode === 'click') {
      // The USF plugin button never submits its form; there is no handler to replay into.
      addDirect(p.form, p.submitter);
      return;
    }

    var submitter = p.submitter && p.submitter.form === p.form ? p.submitter : null;
    var submitEvent;
    try {
      submitEvent = new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: submitter });
    } catch (e) {
      submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    }

    p.form.setAttribute('data-pincode-bypass', '');
    try {
      p.form.dispatchEvent(submitEvent);
    } finally {
      // Listeners run synchronously inside dispatchEvent; never leave the bypass armed.
      p.form.removeAttribute('data-pincode-bypass');
    }
    if (!submitEvent.defaultPrevented) addDirect(p.form, submitter);
  }

  /* ---------- dialog ---------- */

  function setState(state) {
    dialog.setAttribute('data-state', state);
    submitBtn.disabled = state === 'loading';
  }

  function showError(message) {
    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');
    inputEl.setAttribute('aria-invalid', 'true');
    setState('error');
  }

  function clearError() {
    errorEl.setAttribute('hidden', '');
    errorEl.textContent = '';
    inputEl.removeAttribute('aria-invalid');
  }

  function etaRow(icon, html) {
    return '<li><span class="pincode-gate__eta-icon">' + icon + '</span><span>' + html + '</span></li>';
  }

  var ICON_CLOCK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function renderEta(eta) {
    var html = etaRow(ICON_TRUCK, 'Arrives by <strong>' + PD.arrivalText(eta) + '</strong>');
    var countdown = PD.countdownText(eta);
    if (countdown) html += etaRow(ICON_CLOCK, countdown);
    etaEl.innerHTML = html;
  }

  // The cart drawer, quick-buy popover etc. trap focus and close on any outside click. Pause the
  // trap of whatever contains the clicked button, or it would steal focus from the dialog.
  function pauseTraps(form) {
    for (var el = form; el; el = el.parentElement) {
      if (el.tagName.indexOf('-') !== -1 && 'focusTrap' in el) {
        var trap = el.focusTrap;
        if (trap && typeof trap.pause === 'function') {
          trap.pause();
          pausedTraps.push(trap);
        }
      }
    }
  }

  function resumeTraps() {
    pausedTraps.forEach(function (trap) {
      try { trap.unpause(); } catch (e) { /* trap was torn down while paused */ }
    });
    pausedTraps = [];
  }

  function openDialog(saved) {
    clearError();
    etaEl.innerHTML = '';
    inputEl.value = saved || '';
    dialog.setAttribute('data-mode', pending.mode === 'cart' ? 'cart' : 'add');
    setState('idle');
    opener = document.activeElement;
    pauseTraps(pending.anchor || pending.form);
    dialog.open = true;
    // The theme's focus trap activates ~150ms after opening; focus the field once it has.
    setTimeout(function () { inputEl.focus(); }, 200);
  }

  function closeDialog() {
    clearTimeout(proceedTimer);
    proceedTimer = null;
    if (dialog.open) dialog.open = false;
  }

  // Closes the dialog and replays the add. The theme's modal fires its close event synchronously,
  // and that handler clears `pending` (a cancel), so hold on to it across the close.
  function approve() {
    var p = pending;
    closeDialog();
    pending = p;
    proceed();
  }

  function onChecked(pin, result) {
    var p = pending;
    if (!p) return;

    if (result.persist) {
      PD.remember(pin, result.zone, result.info.city, 'gate');
    } else {
      PD.forget();
    }

    if (result.status !== 'ok') {
      showError("Sorry, this product can't be delivered to " + pin + '.');
      return;
    }

    whereEl.textContent = 'Delivering to ' + pin + (result.info.city ? ' · ' + result.info.city : '');
    renderEta(PD.computeEta({ zone: result.zone, isLLS: p.flags.isLLS, sunOk: !!p.flags.panIndia }));
    setState('success');
    proceedTimer = setTimeout(function () {
      proceedTimer = null;
      approve();
    }, 1100);
  }

  // Cart mode: save the pincode for the whole cart. Per-item deliverability is shown by the cart card.
  function onCartChecked(pin) {
    var result = PD.evaluateCart(pin, pending.items);
    if (!result.info) {
      showError("Sorry, we don't deliver to " + pin + '.');
      return;
    }

    PD.remember(pin, result.zone, result.info.city, 'gate');
    whereEl.textContent = 'Delivering to ' + pin + (result.info.city ? ' · ' + result.info.city : '');
    etaEl.innerHTML = '';
    if (result.eta) renderEta(result.eta);
    setState('success');
    proceedTimer = setTimeout(closeDialog, 1100);
  }

  formEl.addEventListener('submit', function (event) {
    event.preventDefault();
    if (!pending) return;

    var pin = inputEl.value.replace(/\D/g, '');
    if (pin.length !== 6) {
      showError('Enter a valid 6-digit pincode.');
      return;
    }

    clearError();
    setState('loading');
    PD.init(cfg);
    PD.load(pin).then(function () {
      if (!pending) return;
      if (pending.mode === 'cart') {
        onCartChecked(pin);
      } else {
        onChecked(pin, PD.evaluate(pin, pending.flags));
      }
    }, function () {
      if (pending && pending.mode === 'cart') {
        showError("Couldn't check right now. Please try again.");
      } else {
        // Sheet unreachable: we can't validate, so don't block the shopper.
        approve();
      }
    });
  });

  inputEl.addEventListener('input', function () {
    inputEl.value = inputEl.value.replace(/\D/g, '');
    if (dialog.getAttribute('data-state') === 'error') {
      clearError();
      setState('idle');
    }
  });

  // Fires for every way of closing: the × button, Esc, the overlay and our own closeDialog().
  dialog.addEventListener('openable-element:close', function () {
    clearTimeout(proceedTimer);
    proceedTimer = null;
    // Reaching here without approve() means the shopper cancelled.
    pending = null;
    resumeTraps();
    if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
    opener = null;
  });

  /* ---------- interception ---------- */

  function intercept(event, form, submitter, mode) {
    var need = needsGate(form);
    if (!need) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    pending = { form: form, submitter: submitter || null, mode: mode, flags: need.flags };

    if (!need.saved) {
      openDialog('');
      return;
    }
    // A pincode is saved but not checked on this page yet: check it quietly first.
    PD.init(cfg);
    PD.load(need.saved).then(function () {
      if (!pending) return;
      if (PD.evaluate(need.saved, pending.flags).status === 'ok') {
        proceed();
      } else {
        openDialog(need.saved);
      }
    }, function () {
      proceed();
    });
  }

  document.addEventListener('submit', function (event) {
    intercept(event, event.target, event.submitter, 'submit');
  }, true);

  // Buttons are intercepted at click time as well as at submit time:
  //  - the USF plugin button handles click with .prevent.stop and never submits its form;
  //  - on Safari the theme's loader-button posts the form natively from its click handler,
  //    which skips the submit event entirely.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('button');
    if (!button || !button.form) return;
    var isPlugin = button.classList.contains('usf-add-to-cart-btn') && button.form.classList.contains('usf-add-to-cart');
    if (!isPlugin && button.type !== 'submit') return;
    if (button.form.querySelector('[data-pincode-flags]') === null) return;
    intercept(event, button.form, button, isPlugin ? 'click' : 'submit');
  }, true);

  // Used by the cart's "Change" link (the cart section below).
  window.PincodeGate = {
    openForCart: function (anchor) {
      var items = Array.prototype.map.call(anchor.querySelectorAll('[data-pincode-flags]'), flagsFromEl);
      pending = { mode: 'cart', anchor: anchor, items: items };
      openDialog(PD.savedPincode() || '');
    }
  };

  // Look up the saved pincode when the browser is idle so the first Add to Cart can be checked instantly.
  var warm = function () {
    PD.init(cfg);
    var saved = PD.savedPincode();
    if (saved) PD.load(saved).catch(function () { /* retried on first use */ });
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(warm, { timeout: 4000 });
  } else {
    setTimeout(warm, 2000);
  }
})();

/*
 * <pincode-cart-info>: the cart's delivery section (snippets/delivery-options.liquid).
 *
 * It wraps the Standard / Choose-a-date widget (assets/datepicker.js) and adds:
 *   - a "DELIVERY TO 560075" header whose pincode opens the pincode popup to change it
 *     (the gate section above);
 *   - notes under it: an order-by countdown, and a warning for items that can't go to that pincode;
 *   - the arrival date shown on the widget's Standard card, passed over `pincode:arrival`.
 *
 * It is a custom element because the cart drawer is re-rendered from server HTML whenever the cart
 * changes: each new copy of the element draws itself as soon as it is connected. It never touches
 * the widget's mount point, so the widget keeps its own state.
 *
 * Who sees what:
 *   India, known pincode ........ header, notes, both cards
 *   India, no pincode ........... hidden (leftover "scheduled" choices are reset so a hidden widget
 *                                 can't block checkout with "Please select a delivery date")
 *   Rakhi-only cart, no pincode . visible with an "Enter pincode" prompt: only scheduled delivery
 *                                 is allowed, so the shopper must be able to pick a date
 *   Outside India ............... the widget alone, no pincode header
 */
(function () {
  var configEl = document.getElementById('pincode-delivery-config-global');
  if (!configEl || !window.PincodeDelivery || window.customElements.get('pincode-cart-info')) return;

  var PD = window.PincodeDelivery;
  var cfg = JSON.parse(configEl.textContent);

  var ICON_TRUCK = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2m0 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m6 0H9m6 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m0 0h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_WARN = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3 2 20h20L12 3Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M12 10v4M12 17v.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Shoppers outside India don't use pincodes (custom.js hides the product page checker for them too).
  function isInternationalVisitor() {
    try {
      var country = localStorage.getItem('userCountry');
      return !!country && country !== 'IN';
    } catch (e) {
      return false;
    }
  }

  // The widget reads the arrival text when it renders and again on this event.
  function publishArrival(detail) {
    window.__pincodeArrival = detail;
    window.dispatchEvent(new CustomEvent('pincode:arrival', { detail: detail }));
  }

  // Puts the widget back on Standard delivery with no date, so a hidden widget can never hold
  // a "scheduled" choice that blocks checkout.
  function resetSchedule(root) {
    try {
      if (localStorage.getItem('delivery_mode') === 'scheduled') localStorage.setItem('delivery_mode', 'standard');
      localStorage.removeItem('delivery_date_iso');
    } catch (e) { /* storage blocked */ }
    document.querySelectorAll('[name="attributes[Delivery Date]"]').forEach(function (input) {
      input.value = '';
    });
    var standard = root.querySelector('.dp_options > button.dp_option');
    if (standard && !standard.disabled && !standard.classList.contains('is-checked')) standard.click();
  }

  function note(icon, html, modifier) {
    return '<p class="pincode-cart-info__note ' + (modifier || '') + '"><span class="pincode-cart-info__note-icon">' + icon + '</span><span>' + html + '</span></p>';
  }

  var PincodeCartInfo = class extends HTMLElement {
    constructor() {
      super();
      this._redraw = this.render.bind(this);
      this.addEventListener('click', function (event) {
        if (!event.target.closest('[data-pincode-change]')) return;
        event.preventDefault();
        if (window.PincodeGate) window.PincodeGate.openForCart(this);
      });
    }

    connectedCallback() {
      document.addEventListener('pincode:changed', this._redraw);
      window.addEventListener('geo:updated', this._redraw);
      // Rolls the date forward once the cutoff passes.
      this._timer = setInterval(this._redraw, 60000);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('pincode:changed', this._redraw);
      window.removeEventListener('geo:updated', this._redraw);
      clearInterval(this._timer);
    }

    render() {
      var self = this;
      PD.init(cfg);
      PD.load(PD.savedPincode() || '').then(function () {
        if (self.isConnected) self.draw();
      }, function () {
        // Sheet unreachable: there is nothing reliable to show, but never hide a widget that is required.
        if (self.isConnected) self.drawWidgetOnly();
      });
    }

    setParts(head, notes) {
      this.querySelector('[data-pincode-cart-head]').innerHTML = head;
      this.querySelector('[data-pincode-cart-notes]').innerHTML = notes;
      this.hidden = false;
    }

    drawWidgetOnly() {
      publishArrival({ text: null, countdown: null, countdownHtml: '', schedule: PD.baseScheduleInfo() });
      this.setParts('', '');
    }

    // Shoppers outside India have no pincode: the estimate comes from the international days setting.
    drawInternational() {
      var eta = PD.intlEta();
      publishArrival({
        text: PD.arrivalText(eta),
        countdown: null,
        countdownHtml: '',
        schedule: PD.scheduleInfo(eta)
      });
      this.setParts('', '');
    }

    draw() {
      if (isInternationalVisitor()) {
        this.drawInternational();
        return;
      }

      var pin = PD.savedPincode();
      var items = Array.prototype.map.call(this.querySelectorAll('[data-pincode-flags]'), function (el) {
        return {
          title: el.getAttribute('data-title') || '',
          panIndia: el.getAttribute('data-pan-india') === 'true',
          notHyderabad: el.getAttribute('data-not-hyderabad') === 'true',
          isLLS: el.getAttribute('data-lls') === 'true'
        };
      });
      var result = pin && items.length ? PD.evaluateCart(pin, items) : null;

      // No saved pincode, or one the sheet doesn't know.
      if (!result || !result.info) {
        publishArrival({ text: null, countdown: null, countdownHtml: '', schedule: PD.baseScheduleInfo() });
        if (this.hasAttribute('data-force-schedule')) {
          this.setParts(
            '<span class="pincode-cart-info__icon">' + ICON_TRUCK + '</span><span>Delivery</span>' +
            '<button type="button" class="pincode-cart-info__pin" data-pincode-change>Enter pincode</button>',
            ''
          );
        } else {
          resetSchedule(this);
          this.hidden = true;
        }
        return;
      }

      var head = '<span class="pincode-cart-info__icon">' + ICON_TRUCK + '</span><span>Delivery to</span>' +
        '<button type="button" class="pincode-cart-info__pin" data-pincode-change aria-label="Change pincode ' + escapeHtml(pin) + '">' + escapeHtml(pin) + '</button>';

      var notes = '';
      if (result.blocked.length) {
        var titles = [];
        result.blocked.forEach(function (flags) {
          if (flags.title && titles.indexOf(flags.title) === -1) titles.push(flags.title);
        });
        notes += note(ICON_WARN, 'Can’t be delivered to ' + escapeHtml(pin) + (titles.length ? ': ' + titles.map(escapeHtml).join(', ') : ''), 'is-warning');
      }

      if (result.eta) {
        // The date picker only offers days after the arrival date.
        publishArrival({
          text: PD.arrivalText(result.eta),
          // Time left to order for the earliest dispatch; null once today's cutoff has passed.
          countdown: result.eta.msLeft === null ? null : PD.formatCountdown(result.eta.msLeft),
          countdownHtml: PD.countdownText(result.eta),
          schedule: PD.scheduleInfo(result.eta)
        });
      } else {
        publishArrival({ text: null, countdown: null, countdownHtml: '', schedule: PD.baseScheduleInfo() });
      }
      this.setParts(head, notes);
    }
  };

  window.customElements.define('pincode-cart-info', PincodeCartInfo);
})();


/*
 * Product page pincode checker (snippets/pincode-checker.liquid): the pincode field, the
 * "Arrives by" estimate with its order-by countdown, and enabling Add to Cart for an eligible pincode.
 * Does nothing on pages without the checker.
 */
(function () {
  var card = document.getElementById('pincode-checker');
  var configEl = document.getElementById('pincode-delivery-config-global');
  var flagsEl = document.querySelector('#pincode-checker ~ [data-pincode-flags]');
  if (!card || !configEl || !flagsEl || !window.PincodeDelivery) return;

  var PD = window.PincodeDelivery;
  var cfg = JSON.parse(configEl.textContent);
  var flags = {
    panIndia: flagsEl.dataset.panIndia === 'true',
    notHyderabad: flagsEl.dataset.notHyderabad === 'true',
    isLLS: flagsEl.dataset.lls === 'true'
  };

  var ICON_CLOCK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_TRUCK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2m0 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m6 0H9m6 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m0 0h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var input = document.getElementById('pincode');
  var button = card.querySelector('.pincodeChecker');
  var delivering = card.querySelector('.delivering');
  var errorEl = card.querySelector('.pincode-card__error');
  var etaEl = card.querySelector('.pincode-card__eta');
  var etaTimer = null;

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // data-state drives what the CSS shows: idle | loading | success | error
  function setState(state) {
    card.setAttribute('data-state', state);
    if (button) {
      button.disabled = state === 'loading';
      button.setAttribute('aria-busy', state === 'loading');
    }
  }

  function clearEta() {
    clearInterval(etaTimer);
    etaTimer = null;
    etaEl.innerHTML = '';
  }

  function clearError() {
    errorEl.hidden = true;
    errorEl.textContent = '';
    input.removeAttribute('aria-invalid');
  }

  function showError(message) {
    clearEta();
    errorEl.textContent = message;
    errorEl.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    setState('error');
  }

  function showSuccess(pin, info, availableHtml) {
    clearError();
    var where = 'to ' + pin + (info.city ? ' · ' + info.city : '');
    // availableHtml is the customizer message and may contain markup; the location text is escaped.
    delivering.innerHTML = '<span class="pincode-card__ok">' + availableHtml + '</span>' +
      '<span class="pincode-card__where">' + escapeHtml(where) + '</span>';
    setState('success');
  }

  function etaRow(icon, html, modifier) {
    return '<li class="pincode-card__eta-row ' + (modifier || '') + '"><span class="pincode-card__eta-icon">' + icon + '</span><span>' + html + '</span></li>';
  }

  // The arrival date and order-by countdown. Re-run every minute so both stay current.
  function renderEta(info) {
    var eta = PD.computeEta({ zone: info.zone, isLLS: flags.isLLS, sunOk: !!flags.panIndia });
    var html = etaRow(ICON_TRUCK, 'Arrives by <strong>' + PD.arrivalText(eta) + '</strong>');
    var countdown = PD.countdownText(eta);
    if (countdown) html += etaRow(ICON_CLOCK, countdown, 'is-accent');
    etaEl.innerHTML = html;
  }

  function startEta(info) {
    clearEta();
    renderEta(info);
    etaTimer = setInterval(function () { renderEta(info); }, 60000);
  }

  // The theme marks a sold-out / unavailable variant's button with button--ternary.
  function isSoldOut(atc) {
    return atc.classList.contains('button--ternary');
  }

  // Add to Cart is only enabled for an eligible, remembered pincode, and never for a sold-out variant.
  function syncAddToCart() {
    var atc = document.querySelector('#AddToCart');
    if (!atc) return;
    var saved = PD.savedPincodeInfo();
    atc.disabled = isSoldOut(atc) || !saved || (saved.zone === 'PANINDIA' && !flags.panIndia);
  }

  function checkPincode() {
    var pin = input.value.replace(/\D/g, '');
    clearEta();

    if (pin.length === 0) {
      showError('Pincode can not be empty.');
      PD.forget();
      return;
    }
    if (pin.length < 6) {
      showError('Enter a valid 6-digit pincode.');
      PD.forget();
      return;
    }

    var result = PD.evaluate(pin, flags);
    if (result.persist) {
      PD.remember(pin, result.zone, result.info.city, 'checker');
    } else {
      PD.forget();
    }

    if (result.status === 'ok') {
      showSuccess(pin, result.info, result.zone === 'HYDERABAD' ? cfg.availableLocal : cfg.availablePan);
      startEta(result.info);
    } else {
      showError('Sorry, this product is not deliverable to ' + pin + '.');
    }
  }

  function runCheck() {
    setState('loading');
    PD.init(cfg);
    PD.load(input.value.replace(/\D/g, '')).then(function () {
      checkPincode();
      syncAddToCart();
    }, function () {
      // Sheet unreachable: don't strand Pan-India shoppers behind a disabled button.
      showError("Couldn't check right now. The delivery estimate will be shown at checkout.");
      var atc = document.querySelector('#AddToCart');
      if (atc && flags.panIndia && !isSoldOut(atc)) atc.disabled = false;
    });
  }

  input.addEventListener('input', function () {
    input.value = input.value.replace(/\D/g, '');
    if (card.getAttribute('data-state') === 'error') {
      clearError();
      setState('idle');
    }
  });
  input.addEventListener('keydown', function (event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      runCheck();
    }
  });
  if (button) button.addEventListener('click', runCheck);
  card.addEventListener('click', function (event) {
    if (!event.target.closest('.pincode-card__change')) return;
    clearEta();
    setState('idle');
    input.focus();
    input.select();
  });

  // The Add to Cart pincode gate saved a pincode: show it here too.
  document.addEventListener('pincode:changed', function (event) {
    if (event.detail.source === 'checker') return;
    input.value = event.detail.pin;
    runCheck();
  });

  // Re-check a pincode remembered from an earlier visit.
  var savedPin = PD.savedPincode();
  if (savedPin !== null) {
    input.value = savedPin;
    runCheck();
  }
})();

/*
 * Product page estimate for shoppers outside India: "Arrives by <date>" from the International
 * days setting, shown where the pincode checker would be (snippets/pincode-checker.liquid).
 */
(function () {
  var box = document.querySelector('[data-pincode-intl-eta]');
  var configEl = document.getElementById('pincode-delivery-config-global');
  if (!box || !configEl || !window.PincodeDelivery) return;

  var PD = window.PincodeDelivery;
  var cfg = JSON.parse(configEl.textContent);
  var list = box.querySelector('.pincode-card__eta-list');
  var ICON_TRUCK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2m0 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m6 0H9m6 0a2 2 0 1 0 4 0m-4 0a2 2 0 1 1 4 0m0 0h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function row(icon, html, modifier) {
    return '<li class="pincode-card__eta-row ' + (modifier || '') + '"><span class="pincode-card__eta-icon">' + icon + '</span><span>' + html + '</span></li>';
  }

  // Re-run every minute so the date rolls past the cutoff.
  function render() {
    PD.init(cfg);
    var eta = PD.intlEta();
    list.innerHTML = row(ICON_TRUCK, 'Arrives by <strong>' + PD.arrivalText(eta) + '</strong>');
    box.hidden = false;
  }

  render();
  setInterval(render, 60000);
})();
